import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

import Form from "../../components/base/Form/Form";
import { Button } from "../../components/base/Button";
import LoadingWheel from "../../components/shared/LoadingWheel";
import { FormErrors } from "../../components/base/Form/FormErrors";
import { FormSection } from "../../components/Register/FormSection";
import { FormTextField } from "../../components/base/Form/FormTextField";
import { Alert } from "../../components/base/Alert";
import { IBrand } from "../../models/Brand/Brand";
import { BrandRequest } from "../../models/auth/BrandRequest";
import { DefaultResponseWithData } from "../../models/system/DefaultResponse";
import { useRegister } from "../../data/Auth/useRegister";
import { useTheme } from "../../contexts/ThemeContext";
import useApiHelper from "../../hooks/useApiHelper";

interface RegisterFormFields {
  company: {
    name: string;
    line1: string;
    line2?: string;
    town: string;
    county?: string;
    postcode: string;
    telephone?: string;
    email?: string;
  };
  user: {
    forename: string;
    surname: string;
    email: string;
    password: string;
    confirmPassword: string;
  };
}

type PageState = "checking" | "unavailable" | "ready" | "submitting";

/**
 * Self-service signup. Must be reached from the login page of an application that allows
 * signup (?app=<applicationId>&returnUrl=<app url>). On success the API sets the auth cookie and we
 * redirect straight into the application, exactly like a normal login.
 */
export default function Register() {
  const { post } = useApiHelper();
  const { setTheme } = useTheme();
  const { register: registerMutation } = useRegister();
  const [urlParams] = useSearchParams();

  const applicationId = urlParams.get("app");
  const returnUrl = urlParams.get("returnUrl");

  const [state, setState] = useState<PageState>("checking");
  const [error, setError] = useState<string>();
  const [unavailableReason, setUnavailableReason] = useState<string>();

  const loginUrl = (() => {
    const query = new URLSearchParams();
    if (applicationId) query.set("app", applicationId);
    if (returnUrl) query.set("returnUrl", returnUrl);
    return `/?${query.toString()}`;
  })();

  useEffect(() => {
    if (!applicationId || !returnUrl) {
      setUnavailableReason(
        "Sign up must be started from the login page of the application you want to use."
      );
      setState("unavailable");
      return;
    }

    (async () => {
      try {
        const result = await post<BrandRequest, DefaultResponseWithData<IBrand>>(
          "/brand/lookup",
          { applicationId }
        );

        if (result.success) setTheme(result.data);

        if (result.success && result.data.allowSignup === true) {
          setState("ready");
        } else {
          setUnavailableReason(
            "Sign up is not available for this application. Please contact support to get set up."
          );
          setState("unavailable");
        }
      } catch {
        setUnavailableReason("We couldn't verify this application, please try again later.");
        setState("unavailable");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRegister = (data: RegisterFormFields) => {
    setError(undefined);
    setState("submitting");

    registerMutation.mutate(
      {
        applicationId: applicationId!,
        companyName: data.company.name,
        line1: data.company.line1,
        line2: data.company.line2 || undefined,
        town: data.company.town,
        county: data.company.county || undefined,
        postcode: data.company.postcode,
        telephone: data.company.telephone || undefined,
        companyEmail: data.company.email || undefined,
        forename: data.user.forename,
        surname: data.user.surname,
        email: data.user.email,
        password: data.user.password,
      },
      {
        onSuccess: (response) => {
          // Cookie is set by the API; hand over to the application with its registration id,
          // mirroring loginWithApplicationId on the login page.
          window.location.assign(
            `${returnUrl}?ari=${response.data.applicationRegistrationId}`
          );
        },
        onError: (err: any) => {
          setState("ready");

          if (err?.status === 409) {
            setError(
              "An account is already registered against this email address. Please log in instead."
            );
          } else if (err?.status === 429) {
            setError("Too many sign up attempts, please wait a few minutes and try again.");
          } else if (err?.status && err?.message) {
            setError(err.message);
          } else {
            setError(
              "We've encountered an error while trying to sign you up. Please try again, and if the issue persists contact support."
            );
          }
        },
      }
    );
  };

  if (state === "checking") {
    return (
      <div className="py-24 flex items-center justify-center">
        <LoadingWheel />
      </div>
    );
  }

  return (
    <div className="prose max-w-6xl mx-auto my-12 pb-12 px-4">
      <h1 className="mb-2 font-bold">Create your account</h1>
      <p className="mt-0 mb-10 text-gray-600">
        Set up your company and your first admin login. You'll be taken straight into the app
        afterwards to finish setting things up.
      </p>

      {state === "unavailable" ? (
        <>
          <Alert severity="error" title="Sign up unavailable" message={unavailableReason ?? ""} />
          <div className="mt-6">
            <Button variant="outlined" onClick={() => window.location.assign(loginUrl)}>
              Back to login
            </Button>
          </div>
        </>
      ) : state === "submitting" ? (
        <div className="py-12 flex items-center justify-center">
          <LoadingWheel />
        </div>
      ) : (
        <Form<RegisterFormFields> className="space-y-6" onSubmit={handleRegister}>
          {({ register, watch }) => (
            <>
              {error && (
                <Alert severity="error" title="Sign up failed" message={error} className="mb-4" />
              )}
              <FormErrors />
              <FormSection
                title="Company information"
                description="We will use this to set up your company account and your address for quotes."
              >
                <div className="space-y-4">
                  <FormTextField
                    {...register("company.name", { required: true })}
                    label="Company Name"
                    autoComplete="organization"
                  />
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormTextField
                      {...register("company.line1", { required: true })}
                      label="Address Line 1"
                      autoComplete="address-line1"
                    />
                    <FormTextField
                      {...register("company.line2")}
                      label="Address Line 2"
                      autoComplete="address-line2"
                    />
                    <FormTextField
                      {...register("company.town", { required: true })}
                      label="Town"
                      autoComplete="address-level2"
                    />
                    <FormTextField {...register("company.county")} label="County" />
                    <FormTextField
                      {...register("company.postcode", { required: true })}
                      label="Postcode"
                      autoComplete="postal-code"
                    />
                    <FormTextField
                      {...register("company.telephone")}
                      label="Telephone"
                      type="tel"
                      autoComplete="tel"
                    />
                  </div>
                  <FormTextField
                    {...register("company.email", {
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Please enter a valid email address",
                      },
                    })}
                    label="Company Email (optional, defaults to your login email)"
                    type="email"
                  />
                </div>
              </FormSection>
              <div className="h-px border" />
              <FormSection
                title="Your login"
                description="This will be the first admin user for your company. You can invite more users once you're in."
              >
                <div className="space-y-4">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormTextField
                      {...register("user.forename", { required: true })}
                      label="Forename"
                      autoComplete="given-name"
                    />
                    <FormTextField
                      {...register("user.surname", { required: true })}
                      label="Surname"
                      autoComplete="family-name"
                    />
                  </div>
                  <FormTextField
                    {...register("user.email", {
                      required: true,
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: "Please enter a valid email address",
                      },
                    })}
                    label="Email Address"
                    type="email"
                    autoComplete="email"
                  />
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <FormTextField
                      {...register("user.password", {
                        required: true,
                        minLength: {
                          value: 8,
                          message: "Password must be at least 8 characters",
                        },
                        pattern: {
                          value: /\d/,
                          message: "Password must contain at least 1 number",
                        },
                      })}
                      type="password"
                      label="Password"
                      autoComplete="new-password"
                    />
                    <FormTextField
                      {...register("user.confirmPassword", {
                        required: true,
                        validate: {
                          matches: (v) =>
                            v === watch("user.password") ||
                            "Confirm Password must match Password",
                        },
                      })}
                      type="password"
                      label="Confirm Password"
                      autoComplete="new-password"
                    />
                  </div>
                </div>
              </FormSection>
              <div className="grid grid-cols-1 gap-x-8 gap-y-8 md:grid-cols-3">
                <div />
                <div className="sm:rounded-xl md:col-span-2 space-y-3">
                  <Button fullWidth type="submit">
                    Create account
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={(e) => {
                      e.preventDefault();
                      window.location.assign(loginUrl);
                    }}
                  >
                    Already have an account? Log in
                  </Button>
                </div>
              </div>
            </>
          )}
        </Form>
      )}
    </div>
  );
}
